import Bowser from 'bowser';
import mixpanel from 'mixpanel-browser';

import { getAppVersion } from '../utils/appVersion';
import { LanguageUtils } from '../utils/translations';
import { consoleError } from '../utils/utils';

type EventProps = { [key: string]: string | string[] | number | number[] | boolean };
type PropsWithProviderId = EventProps & { providerId: number };
type PropsWithLeadId = EventProps & { leadToken: string };

/**
 * Returns set of properties, required for tracking details of user's environment.
 * Device & Browser in particular.
 */
const getDeviceInfo = (() => {
  const refresh = () => {
    const browser = Bowser.parse(window.navigator.userAgent);

    return {
      browser_name: browser.browser.name,
      platform_type: browser.platform.type,
      platform_model: browser.platform.model,
      platform_vendor: browser.platform.vendor,
      os_name: browser.os.name,
      os_version: browser.os.version,
      device_orientation: window.matchMedia('(orientation: portrait)').matches
        ? 'portrait'
        : 'landscape',
    };
  };

  let value = refresh();

  // We don't want to call `refresh` on every function call
  let shouldBeUpdated = false;

  // Props needs to be refreshed on browser rotation
  window.addEventListener('orientationchange', () => {
    shouldBeUpdated = true;
  });

  return () => {
    if (shouldBeUpdated) {
      value = refresh();
      shouldBeUpdated = false;
    }
    return value;
  };
})();

/**
 * We don't want `track` method to be part of Mixpanel's public methods, and
 * eslint has some schizophrenic setting where he wants to have two conflicting things:
 *  - mock `track` as a method of MixpanelService because it is there
 *  - do not mock `private` method
 * this is why we will keep this piece of code as a separate function
 */
function track(name: string, props: EventProps) {
  if (!process.env.REACT_APP_MIXPANEL_TOKEN) {
    return;
  }
  try {
    const appLocale = LanguageUtils.getAppLocaleWithFallback();
    const { appVersion, moduleName, moduleVersion } = getAppVersion();

    mixpanel.track(name, {
      ...getDeviceInfo(),
      ...props,
      appLocale,
      appVersion,
      moduleName,
      moduleVersion,
    });
  } catch (e) {
    consoleError('Failed to track MixPanel event', e);
  }
}

export class MixpanelService {
  constructor() {
    mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN || '');
  }

  public identify(distinctId: string) {
    mixpanel.identify(distinctId);
  }

  public reset() {
    mixpanel.reset();
  }

  public alias(newId: string, oldId: string) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::alias] ', newId, oldId);
    }
    mixpanel.alias(newId, oldId);
  }

  /**
   * Majority of events planned to be tracked, are required to pass providerId
   * To mitigate human factor, we will use this method for tracking any mixpanel events,
   * until we get to the case that conflicts with this assumption
   */
  public trackProviderEvent(name: string, props: PropsWithProviderId) {
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::trackProviderEvent] ', name, JSON.stringify(props, null, 2));
    }
    return track(name, props);
  }

  public trackLeadEvent(name: string, props: PropsWithLeadId) {
    if (!props.leadToken) {
      return;
    }
    if (process.env.REACT_APP_STAGE !== 'live') {
      // eslint-disable-next-line no-console -- need it here
      console.log('[Mixpanel::trackLeadEvent] ', name, JSON.stringify(props, null, 2));
    }
    track(name, props);
  }
}
